// Step-by-step directions for a computed route.
//
// Ported from Welcome Day's buildDirections so the pathfinder can surface the
// same human turn-by-turn list. Copy-out clean: this file only imports from
// ./geo.js (never from outside src/pathfinder/).
//
// The core rule is the "intersection" account: a nav node typed 'intersection'
// is an AUTHORED decision point, so a turn (left/right/straight, derived from
// the path geometry there) is announced at intersections ALWAYS. Elsewhere it
// falls back to a heuristic — a real crossroad (degree ≥ 3) where the path
// bends at least TURN_DEG. Plain corridor bends are not announced.

import { haversineMeters } from './geo.js';

// Tunnels live on floor -1 but read as "Level 100" to the user; floor 0 = outside.
const dispFloor = f => (f === -1 ? 1 : (f ?? 0));
const fmtDist = m => (m == null ? null : m < 950 ? `${Math.round(m / 10) * 10} m` : `${(m / 1000).toFixed(1)} km`);

const TURN_DEG = 35;  // a bend this sharp (at a junction) is announced as a turn
const WALK_MIN = 40;  // only call out a walking stretch once it's at least this long (m)
// Two opposite turns closer together than this are one jog, not two turns.
const ZIGZAG_METERS = 5;

// Angle (deg) of the turn at b when walking a→b→c, and which way it bends.
// lng is scaled by cos(lat) so the angle is measured in true ground geometry.
function turnInfo(a, b, c) {
  const k = Math.cos((b.lat * Math.PI) / 180);
  const v1 = { x: (b.lng - a.lng) * k, y: b.lat - a.lat };
  const v2 = { x: (c.lng - b.lng) * k, y: c.lat - b.lat };
  if (Math.hypot(v1.x, v1.y) < 1e-9 || Math.hypot(v2.x, v2.y) < 1e-9) return null;
  const cross = v1.x * v2.y - v1.y * v2.x;   // >0 ⇒ counter-clockwise ⇒ left
  const dot = v1.x * v2.x + v1.y * v2.y;
  const angle = (Math.atan2(Math.abs(cross), dot) * 180) / Math.PI;
  return { angle, dir: cross > 0 ? 'left' : 'right' };
}

/**
 * Turn a computed route into human step-by-step directions. Derives:
 *  · Enter/Exit BUILDING at exit nodes (outside↔building boundaries)
 *  · Take the stairs/elevator from Level X to Level Y (route.transitions)
 *  · Enter the tunnels from X to Y (tunnel pseudo-building runs)
 *  · Turn left/right/straight at intersections (always) and sharp crossroads
 *
 * @param route   the object from findRoute (nodes, transitions)
 * @param md      mapData from buildMapData (buildings, adjacency)
 * @returns Array<{id, icon, text, chip}> — [] when there's nothing to say
 */
export function buildDirections(route, md) {
  const nodes = route?.nodes || [];
  if (nodes.length < 2) return [];
  const isOutside = n => !n.building || n.building === 'outdoors' || n.floor === 0;
  const nameOf = slug => {
    if (!slug || slug === 'outdoors') return 'outside';
    if (slug === 'tunnels') return 'the tunnels';
    return md?.buildings?.find(x => x.slug === slug)?.name || slug;
  };
  const levelText = f => (dispFloor(f) >= 1 ? `Level ${dispFloor(f) * 100}` : 'outside');
  const levelChip = f => (dispFloor(f) >= 1 ? `L${dispFloor(f) * 100}` : 'Outside');

  // Shaft floor-changes are edges; index them both directions by node-id pair.
  const trans = new Map();
  for (const t of route?.transitions || []) {
    if (t.fromNode?.id && t.toNode?.id) {
      trans.set(`${t.fromNode.id}>${t.toNode.id}`, t);
      trans.set(`${t.toNode.id}>${t.fromNode.id}`, t);
    }
  }

  const steps = [];
  const at = n => ({ lat: n.lat, lng: n.lng }); // map-focus point for a step
  // Nodes that already have a step, so a photo at one hangs its binoculars on
  // the real instruction instead of adding a bare "look" line beside it.
  const spoken = new Set();
  // meta (optional): { at, nodeId, floor } lets the UI pan/zoom to a step.
  // `force` appends even when the text repeats — photo steps are per-node, and
  // two in a row would otherwise collapse into one and strand the second.
  const push = (icon, text, chip = null, meta = null, force = false) => {
    const last = steps[steps.length - 1];
    if (force || !last || last.text !== text) {
      steps.push({ id: steps.length, icon, text, chip, ...(meta || {}) });
      if (meta?.nodeId) spoken.add(meta.nodeId);
    }
  };

  // Straight-line distance accumulates between instructions and is flushed as a
  // "Walk Xm" step once it's long enough — so directions read "walk 500 m, then…".
  let walk = 0, walkFloor = null;
  const flushWalk = () => {
    if (walk >= WALK_MIN && dispFloor(walkFloor) >= 1) push('🚶', `Walk ${fmtDist(walk)}`, levelChip(walkFloor));
    walk = 0; walkFloor = null;
  };
  const degree = n => (md?.adjacency?.get(n.id)?.length || 0); // a junction ⇒ ≥3

  // ── Which nodes would be announced as turns ───────────────────────────────
  // Worked out up front so opposite turns a couple of steps apart can be seen
  // as the pair they are. Mirrors the conditions the emit loop uses below.
  const turnAt = new Map(); // node index → turnInfo
  for (let i = 1; i < nodes.length - 1; i++) {
    const prev = nodes[i - 1], cur = nodes[i];
    if (isOutside(cur)) continue;
    if (trans.get(`${prev.id}>${cur.id}`)) continue;      // a shaft, not a turn
    const ti = turnInfo(prev, cur, nodes[i + 1]);
    if (!ti || ti.angle < TURN_DEG) continue;
    if (cur.type === 'intersection' || degree(cur) >= 3) turnAt.set(i, ti);
  }

  // A left immediately followed by a right (or the reverse) is not two turns.
  // It is one jog — stepping around a pillar, or crossing a corridor to the
  // door opposite — and the graph records the two ends of it as sharp bends.
  // Announcing them tells someone to turn twice in the space of a few paces,
  // which is worse than saying nothing: they will look for a junction that
  // isn't there. Both ends are dropped, and because this happens before the
  // emit loop the walking distance flows straight through them, so the next
  // instruction's "After X m" still measures from the last real turn.
  const suppressed = new Set();
  const turnIdx = [...turnAt.keys()].sort((a, b) => a - b);
  for (let k = 0; k + 1 < turnIdx.length; k++) {
    const i = turnIdx[k], j = turnIdx[k + 1];
    if (turnAt.get(i).dir === turnAt.get(j).dir) continue;   // both left: a real dogleg
    let d = 0;
    for (let m = i; m < j; m++) d += haversineMeters(nodes[m], nodes[m + 1]);
    if (d > ZIGZAG_METERS) continue;
    suppressed.add(i); suppressed.add(j);
    k += 1; // j is spoken for; don't also pair it with the turn after it
  }

  for (let i = 1; i < nodes.length; i++) {
    const prev = nodes[i - 1], cur = nodes[i];
    const t = trans.get(`${prev.id}>${cur.id}`); // shaft floor-change edge (vertical, not walking)
    // Only step-by-step INDOORS: accumulate walking distance on indoor edges only.
    if (!t && !isOutside(prev) && !isOutside(cur)) { if (walkFloor == null) walkFloor = prev.floor; walk += haversineMeters(prev, cur); }

    // Building boundary between prev → cur.
    if (prev.building !== cur.building) {
      if (cur.building === 'tunnels') {
        let j = i; while (j < nodes.length && nodes[j].building === 'tunnels') j++;
        const exitB = j < nodes.length ? nodes[j].building : null;
        flushWalk();
        push('🚇', `Enter the tunnels from ${nameOf(prev.building)} to ${nameOf(exitB)} (${levelText(cur.floor)})`,
          null, { at: at(cur), nodeId: cur.id, floor: cur.floor });
      } else if (prev.building === 'tunnels') {
        /* arrival building already named at tunnel entry */
      } else if (isOutside(prev) && !isOutside(cur)) {
        flushWalk();
        push('🚪', `Enter ${nameOf(cur.building)} (${levelText(cur.floor)})`,
          null, { at: at(cur), nodeId: cur.id, floor: cur.floor });
      } else if (!isOutside(prev) && isOutside(cur)) {
        flushWalk();
        push('🚪', `Exit ${nameOf(prev.building)} (${levelText(prev.floor)})`,
          null, { at: at(prev), nodeId: prev.id, floor: prev.floor });
      } else {
        flushWalk();
        push('🚪', `Go from ${nameOf(prev.building)} into ${nameOf(cur.building)}`, levelChip(cur.floor),
          { at: at(cur), nodeId: cur.id, floor: cur.floor });
      }
    }

    // Stair / elevator floor change on this edge — call out the direction (up/down).
    if (t) {
      flushWalk();
      const fromL = dispFloor(t.fromFloor) * 100, toL = dispFloor(t.toFloor) * 100;
      const dir = toL > fromL ? 'up' : 'down';
      const text = t.kind === 'elevator'
        ? `Take the elevator ${dir} to Level ${toL}`
        : `Take the stairs ${dir} to Level ${toL}`;
      push(t.kind === 'elevator' ? '🛗' : '🪜', text, `L${toL}`,
        { at: at(t.toNode), nodeId: t.toNode.id, floor: t.toFloor });
      continue; // don't also report a turn at a shaft node
    }

    // Turns are announced at INTERSECTION nodes (the authored decision points)
    // and at fallback crossroads (degree ≥ 3 bending ≥ TURN_DEG). Only TURNS are
    // called out — a straight pass through an intersection gets no step.
    if (i < nodes.length - 1 && !isOutside(cur)) {
      const ti = turnAt.get(i);
      const isX = cur.type === 'intersection';
      if (ti && !suppressed.has(i)) {
        // Fold the walk up to this turn into the instruction — "After 40 m, turn
        // left …" — so the user knows how far to go before turning. Consumes the
        // pending walk instead of emitting a separate "Walk" step.
        const pending = dispFloor(walkFloor) >= 1 ? walk : 0;
        const after = pending >= 8 ? `After ${fmtDist(pending)}, ` : '';
        walk = 0; walkFloor = null;
        const inside = cur.building !== 'tunnels' ? ` inside ${nameOf(cur.building)}` : '';
        push(ti.dir === 'left' ? '↰' : '↱', `${after}${after ? 'turn' : 'Turn'} ${ti.dir}${isX ? ' at the intersection' : inside}`, levelChip(cur.floor),
          { at: at(cur), nodeId: cur.id, floor: cur.floor });
      }
    }

    // A photo along the way earns its own step when nothing else was said here,
    // so every panorama on the route is reachable from the list and not only by
    // spotting its pin on the map. Most sit on plain corridor nodes that no
    // other rule announces. Where a turn, door or shaft already spoke for this
    // node, that step carries the binoculars instead — one line, not two.
    // The pending walk folds in the same way a turn's does, so the distance to
    // the next instruction still counts from here.
    if (cur.panorama?.image && !spoken.has(cur.id) && i < nodes.length - 1 && !isOutside(cur)) {
      const pending = dispFloor(walkFloor) >= 1 ? walk : 0;
      const after = pending >= 8 ? `After ${fmtDist(pending)}, ` : '';
      walk = 0; walkFloor = null;
      // These only ever land where no turn was announced, so the instruction is
      // "keep going" — and that is what the step should say. The photo is an
      // aid, not the reason to be here, so it stays a binoculars badge on the
      // row rather than the instruction itself; "look around here" told the
      // user to stop and sightsee in the middle of walking somewhere.
      push('↑', `${after}${after ? 'go' : 'Go'} straight`, levelChip(cur.floor),
        { at: at(cur), nodeId: cur.id, floor: cur.floor }, true);
    }
  }
  flushWalk();
  const last = nodes[nodes.length - 1];
  if (!isOutside(last) && last.building !== 'tunnels') {
    push('🏁', `Arrive at ${nameOf(last.building)}${last.room_number ? ` ${last.room_number}` : ''}`, levelChip(last.floor),
      { at: at(last), nodeId: last.id, floor: last.floor });
  }
  return steps;
}
